import { useCallback, useEffect, useState } from 'react';

import { Root, createRoot } from 'react-dom/client';

import ToastMessage from 'src/components/toastMessage';
import { colors } from 'src/styles/colors';
import { MediumFont, RegularFont } from 'src/styles/font';
import { CommerceLandscapeLayoutProps } from 'src/types/commerce';
import { commerceCTA } from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { DEVICE_WIDTH_CONSTANTS } from 'src/utils/constants/utilConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import { MIXPANEL_CLOSE } from '../../../../utils/analytics/mixpanel/mixpanelConstants';
import CommerceSideBar from '../../CommerceSideBar';
import {
  adjustShopIconContainer,
  adjustShopIconContainerOnChromecast
} from './commerceIconHelper';

const CommerceLandscapeLayout: React.FC<CommerceLandscapeLayoutProps> = ({
  onPlayer = false,
  closeLayout,
  handleCommerceLayout
}) => {
  const [root, setRoot] = useState<Root>();
  const [showToast, setShowToast] = useState<boolean>(false);

  const {
    commerceComponentData: commerceData,
    translations,
    commerceWishlistedProductsLength,
    setCommerceWishlistedProductsLength,
    pageLoadPingUrl
  } = useAppStore();

  const videoTag = document.getElementById('video-tag');

  const { plex: { ContextualCommerce_WishlistAddedToast = '' } = {} } =
    translations || {};

  const isMediaLg =
    window?.innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200;
  const isMedialXl =
    window?.innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1800;

  useEffect(() => {
    if (showToast) {
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    }
  }, [showToast]);

  useEffect(() => {
    if (videoTag) {
      const commerceContainer = document.createElement('div');
      const commerceContainerRoot = createRoot(commerceContainer);
      setRoot(commerceContainerRoot);
      commerceContainer.setAttribute('id', 'commerceContainer');
      commerceContainer.setAttribute(
        'style',
        `height: 100%; width: 35%; margin-left: auto; margin-top: -1.5%`
      );
      const commerceContainerId = document.getElementById('commerceContainer');
      if (commerceContainerId && videoTag.contains(commerceContainerId)) {
        videoTag?.removeChild(commerceContainerId);
      }
      videoTag?.appendChild(commerceContainer);
      adjustShopIconContainer();
      detectChromeCast();
    }
  }, [videoTag]);

  const closeLayoutHandler = useCallback(() => {
    adjustShopIconContainer(true);
    detectChromeCast();
    closeLayout();
    commerceCTA(MIXPANEL_CLOSE);
  }, []);

  const detectChromeCast = () => {
    const isChromeCastAvailable = document.querySelector('.chromeCastIcon');
    if (isChromeCastAvailable) {
      adjustShopIconContainerOnChromecast(true);
    } else {
      adjustShopIconContainerOnChromecast(false);
    }
  };

  root?.render(
    <>
      <CommerceSideBar
        data={commerceData}
        onPlayer={onPlayer}
        translations={translations}
        pageLoadPingUrl={pageLoadPingUrl}
        commerceWishlistedProductsLength={commerceWishlistedProductsLength}
        setShowToast={setShowToast}
        handleCommerceLayout={handleCommerceLayout}
        closeLayout={closeLayoutHandler}
        setCommerceWishlistedProductsLength={
          setCommerceWishlistedProductsLength
        }
      />
      {showToast && (
        <ToastMessage
          container="video-tag"
          width="328px"
          textAlign="left"
          maxWidth="352px"
          padding="12px 16px"
          duration={3000}
          fontWeight={isMedialXl ? 400 : 500}
          fontFamily={isMedialXl ? RegularFont : MediumFont}
          fontSize={isMediaLg ? (isMedialXl ? 16 : 14) : 12}
          lineHeight={isMediaLg ? (isMedialXl ? 24 : 20) : 18}
          textColor={colors.PrimaryWhiteColor}
          margin={
            isMediaLg
              ? isMedialXl
                ? '0 0 0 48px'
                : '0 0 0 32px'
              : '0 0 0 20px'
          }
          backgroundColor={colors.AccentDarkGrayColor}
          message={ContextualCommerce_WishlistAddedToast}
        />
      )}
    </>
  );

  return null;
};

export default CommerceLandscapeLayout;
